/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

/**
 * Playback trick modes.
 */
export enum PlaybackFastForwardMode {
  /**
   * Policy value enforcing the rule that the player will replay all the ad breaks
   * that were leaped over by a user during trick mode fast forward operation.
   */
  TRICK_FW_PLAY_ALL = 100,
  /**
   * Policy value enforcing the rule that the player will replay only the first ad break
   * that was leaped over by a user during trick mode fast forward operation.
   */
  TRICK_FW_PLAY_FIRST = 101,
  /**
   * Policy value enforcing the rule that the player will replay only the last ad break
   * that was leaped over by a user during trick mode fast forward operation.
   */
  TRICK_FW_PLAY_LAST = 102,
  /**
   * Policy value enforcing the rule that the player will skip all the ad breaks
   * that was leaped over by a user during trick mode fast forward operation.
   */
  TRICK_FW_SKIP_ALL = 103,
}

/**
 * Playback rewind modes.
 */
export enum PlaybackRewindMode {
  /**
   * Policy value enforcing the rule that the player will replay all the ad breaks
   * that were leaped over by a user during trick mode rewind operation.
   */
  TRICK_RW_PLAY_ALL = 200,
  /**
   * Policy value enforcing the rule that the player will replay only the first ad break
   * that was leaped over by a user during trick mode rewind operation.
   */
  TRICK_RW_PLAY_FIRST = 201,
  /**
   * Policy value enforcing the rule that the player will replay only the last ad break
   * that was leaped over by a user during trick mode rewind operation.
   */
  TRICK_RW_PLAY_LAST = 202,
  /**
   * Policy value enforcing the rule that the player will skip all the ad breaks
   * that was leaped over by a user during trick mode rewind operation.
   */
  TRICK_RW_SKIP_ALL = 203,
}

/**
 * Playback auto seek modes.
 */
export enum PlaybackAutoSeekMode {
  /**
   * Policy value enforcing the rule that the player will start the content without
   * forcing a user to watch any ads that are available from the start of the content
   * to the bookmarked time. Ad breaks encountered later in the playback will still
   * be handled according to the [PlaybackFastForwardRule] policy value.
   */
  AUTO_SEEK_SKIP_ALL = 300,
  /**
   * Policy value enforcing the rule that the player will force a user to watch
   * the [AdContentTimePosition.PREROLL] ads, while any [AdContentTimePosition.MIDROLL]
   * ads, that are available from the start of the content to the bookmarked time,
   * will be skipped. Whether the user has to watch the remaining [AdContentTimePosition.MIDROLL]
   * ads is determined by the [PlaybackFastForwardRule] policy value.
   */
  AUTO_SEEK_PLAY_PREROLL = 301,
  /**
   * Policy value enforcing the rule that the player will force a user to watch
   * at least one [AdContentTimePosition.MIDROLL] ad, while the [AdContentTimePosition.PREROLL]
   * ads will be skipped. Whether the user has to watch all triggered [AdContentTimePosition.MIDROLL] ads,
   * or only one, is determined by the [PlaybackFastForwardRule] policy value.
   */
  AUTO_SEEK_PLAY_MIDROLL = 302,
  /**
   * Policy value enforcing the rule that the player will force a user to watch
   * the [AdContentTimePosition.PREROLL] ads, then one or more [AdContentTimePosition.MIDROLL] ads.
   * Whether the user has to watch all triggered [AdContentTimePosition.MIDROLL] ads,
   * or only one, is determined by the [PlaybackFastForwardRule] policy value.
   */
  AUTO_SEEK_PLAY_ALL = 303,
  /**
   * Policy value enforcing the rule that player will force a user to watch
   * the last ad before the auto seek position. Unlike other rules
   */

  AUTO_SEEK_PLAY_LAST = 304,
}

/**
 * Playback repeated modes.
 */
export enum PlaybackRepeatedMode {
  /**
   * Policy value enforcing the rule that the player will repeatedly play the ad break,
   * with a given ID, in a single playback session whenever the ad break is triggered.
   */
  REPEAT_ALWAYS_PLAY = 400,
  /**
   * Policy value enforcing the rule that the player will play the ad break, with
   * a given ID, only once in a single playback session whenever the ad break is triggered.
   */
  REPEAT_PLAY_ONCE = 401,
}

/**
 * Playback interrupted modes.
 */
export enum PlaybackInterruptedMode {
  /**
   * Policy value enforcing the rule that the player will skip the ad break
   * that was interrupted e.g. switch to another application, etc.
   */
  INTERRUPT_SKIP = 500,
  /**
   * Policy value enforcing the rule that the player will replay the ad break
   * that was interrupted e.g. switch to another application, etc.
   */
  INTERRUPT_RESTART = 501,
  /**
   * Policy value enforcing the rule that the player will resume the ad break
   * that was interrupted e.g. switch to another application, etc.
   */
  INTERRUPT_RESUME = 502,
}
